import locale from "@bonliva-crm/locale";
import {
  AddressInput,
  ConsultantContractAccommodationAlt,
  ConsultantContractBonus,
  ConsultantContractBonusType,
  ConsultantContractCurrency,
  ConsultantContractFormInput,
  ConsultantContractIntroductionPrice,
  ConsultantContractOvertimeOpt,
  ConsultantContractPeriod,
  ConsultantContractSalaryType,
  ConsultantContractTravelsAlt,
  ConsultantContractTravelsPeriod,
  ConsultantContractType,
  ConsultantGetsPaidPer,
  ContractsPriceTemplateFragment,
  ContractType,
  Currency,
  SpecificPeriodPriceInput,
  ValueLabel
} from "@graphql";
import { ContractPriceTemplateRecordInput } from "@config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { FormStore } from "@modules/formx";
import { toFinite } from "lodash";
import { Tenant } from "@bonliva-crm/shared";

export type FormData = Omit<
  ConsultantContractFormInput,
  "HospitalRef" | "DepartmentsRefs" | "YoEx" | "ConsultantId" | "editsProofFileIds"
> & {
  HospitalRef?: ValueLabel;
  YoEx?: string;
  DepartmentsRefs?: string[];
  ConsultantId: string;
  template: string;
  favTemplate: string;
  customerTemplate: string;
  editsProof: {
    files: File[];
    approve: boolean | null;
  };
  _id?: string;
  bonusType?: string;
};

export const BC_contractsTypes = [
  ConsultantContractType.BC_Framework,
  ConsultantContractType.BC_Intermittent,
  ConsultantContractType.BC_Framework_Confirmation,
  ConsultantContractType.BC_Intermittent_Confirmation
];

export const National_contractsTypes = [
  ConsultantContractType.Outsource_National,
  ConsultantContractType.Framework_National,
  ConsultantContractType.Confirmation_National,
  ConsultantContractType.Employment_National,
  ConsultantContractType.Intermittent_National,
  ConsultantContractType.ConfirmationForEmployees_National
];

export const listOfContractStrategyHourly = [
  "EmploymentContractStrategy",
  "EmploymentUskContractStrategy",
  "IntermittentContractStrategy",
  "IntermittentUskContractStrategy",
  "ConfirmationForEmployeesContractStrategy",
  "EmploymentNationalSplitContractStrategy",
  "IntermittentNationalSplitContractStrategy",
  "ConfirmationForEmployeesNationalContractStrategy",
  "EmploymentNationalFullContractStrategy",
  "IntermittentNationalFullContractStrategy"
];

export const splitStrategies = [
  "EmploymentNationalSplitContractStrategy",
  "IntermittentNationalSplitContractStrategy"
];

export const Confirmation_contractsTypes = [
  ConsultantContractType.Confirmation,
  ConsultantContractType.ConfirmationForEmployees,
  ConsultantContractType.BC_Framework_Confirmation,
  ConsultantContractType.BC_Intermittent_Confirmation,
  ConsultantContractType.Confirmation_National,
  ConsultantContractType.ConfirmationForEmployees_National
];

type PartialStatusRecord<K extends string, V> = {
  [P in K]?: V;
};

type NationalLikeContractType = `${ConsultantContractType}_National`;

const defaultToNationalContractsMap: PartialStatusRecord<
  ConsultantContractType,
  NationalLikeContractType
> = {
  [ConsultantContractType.Framework]: ConsultantContractType.Framework_National,
  [ConsultantContractType.Intermittent]: ConsultantContractType.Intermittent_National,
  [ConsultantContractType.Outsource]: ConsultantContractType.Outsource_National,
  [ConsultantContractType.Employment]: ConsultantContractType.Employment_National
};

const nationalToDefaultContractsMap: PartialStatusRecord<
  NationalLikeContractType,
  ConsultantContractType
> = {
  [ConsultantContractType.Framework_National]: ConsultantContractType.Framework,
  [ConsultantContractType.Intermittent_National]: ConsultantContractType.Intermittent,
  [ConsultantContractType.Outsource_National]: ConsultantContractType.Outsource,
  [ConsultantContractType.Employment_National]: ConsultantContractType.Employment
};

export function formatContractTypeByNational(
  type: ConsultantContractType,
  toNational = false
) {
  const map = toNational ? defaultToNationalContractsMap : nationalToDefaultContractsMap;

  return map[type] || type;
}

export type CompanyName = "AB" | "BC";
export type RadioTrueFalse = "true" | "false";

export const getConfirmationContractType = (type: ConsultantContractType) => {
  switch (type) {
    case ConsultantContractType.Framework:
      return ConsultantContractType.Confirmation;
    case ConsultantContractType.Intermittent:
      return ConsultantContractType.ConfirmationForEmployees;
    case ConsultantContractType.BC_Framework:
      return ConsultantContractType.BC_Framework_Confirmation;
    case ConsultantContractType.BC_Intermittent:
      return ConsultantContractType.BC_Intermittent_Confirmation;
    case ConsultantContractType.Framework_National:
      return ConsultantContractType.Confirmation_National;
    case ConsultantContractType.Intermittent_National:
      return ConsultantContractType.ConfirmationForEmployees_National;
    default:
      return null;
  }
};

const defaultMaxCost = {
  Price: 0,
  Currency: Currency.SEK,
  Period: ConsultantContractPeriod.Week,
  SalaryType: ConsultantContractSalaryType.GrossSalary
};

const defaultTravelsMaxCost = {
  Price: 0,
  Currency: Currency.SEK,
  Period: ConsultantContractTravelsPeriod.Week,
  SalaryType: ConsultantContractSalaryType.GrossSalary
};

const defaultAccommodation = {
  Type: null,
  MaxCost: defaultMaxCost,
  PossibleSupplement: ""
};

const defaultTravels = {
  Type: null,
  MaxCost: defaultTravelsMaxCost,
  PossibleSupplement: "",
  TravelTemplateValue: null
};

export const bonusBlank: ConsultantContractBonus = {
  Type: "",
  SalaryType: ConsultantContractBonusType.OtherBonus,
  Config: {},
  Amount: 0,
  Currency: Currency.SEK
};

export const introductionBlank: ConsultantContractIntroductionPrice = {
  Description: "Introduktion",
  Sum: null,
  Currency: Currency.SEK
};

export const initAddress: AddressInput = {
  City: "",
  Country: "",
  StreetName: "",
  ZipCode: ""
};

export const contractSubTypeOptions: ValueLabel[] = [
  {
    label: locale.consultantContractTypes.Outsource,
    value: ConsultantContractType.Outsource
  },
  {
    label: locale.consultantContractTypes.Framework,
    value: ConsultantContractType.Framework
  }
];

export const contractEmployeesTypeOptions: ValueLabel[] = [
  {
    label: locale.consultantContractTypes.Employment,
    value: ConsultantContractType.Employment
  },
  {
    label: locale.consultantContractTypes.Intermittent,
    value: ConsultantContractType.Intermittent
  }
];

export const notional_contractSubTypeOptions: ValueLabel[] = [
  {
    label: locale.consultantContractTypes.Outsource_National,
    value: ConsultantContractType.Outsource_National
  },
  {
    label: locale.consultantContractTypes.Framework_National,
    value: ConsultantContractType.Framework_National
  }
];

export const notional_contractEmployeesTypeOptions: ValueLabel[] = [
  {
    label: locale.consultantContractTypes.Employment_National,
    value: ConsultantContractType.Employment_National
  },
  {
    label: locale.consultantContractTypes.Intermittent_National,
    value: ConsultantContractType.Intermittent_National
  }
];

export const BC_contractEmployeesTypeOptions: ValueLabel[] = [
  {
    label: locale.consultantContractTypes.Intermittent,
    value: ConsultantContractType.BC_Intermittent
  }
];

export const BC_contractSubTypeOptions: ValueLabel[] = [
  {
    label: locale.consultantContractTypes.Framework,
    value: ConsultantContractType.BC_Framework
  }
];

export const langOptions: ValueLabel[] = [
  {
    label: locale.consultantContractLangOpt.v1,
    value: locale.consultantContractLangOpt.v1
  },
  {
    label: locale.consultantContractLangOpt.v2,
    value: locale.consultantContractLangOpt.v2
  }
];

export function getConsultantContractTravelsAltOptions(
  tenant: Tenant,
  isSubcontractor?: boolean,
  showTravelTemplateValue?: boolean
) {
  const opt = [
    ConsultantContractTravelsAlt.BonlivaPayTravel,
    ConsultantContractTravelsAlt.BonlivaPayTravelAllowance,
    ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt,
    ConsultantContractTravelsAlt.SupplierPayTravel,
    ConsultantContractTravelsAlt.ConsultantTaxCompensationAllowance
  ];
  if (showTravelTemplateValue) {
    opt.push(ConsultantContractTravelsAlt.TravelTemplate);
  }
  const _opt = opt.map((v) => ({
    label: !isSubcontractor
      ? locale.consultantContractTravelsAlt[v].replace("Leverantören", "Konsulten")
      : locale.consultantContractTravelsAlt[v],
    value: v
  }));
  if (tenant === Tenant.WCC) {
    return _opt.map((o) => ({ ...o, label: o.label.replace("Bonliva", "WCC") }));
  }
  return _opt;
}

export function getConsultantContractAccAltOptions(
  tenant: Tenant,
  isSubcontractor?: boolean
) {
  const _opt = Object.values(ConsultantContractAccommodationAlt).map((v) => ({
    label: !isSubcontractor
      ? locale.consultantContractAccommodationAlt[v].replace("Leverantören", "Konsulten")
      : locale.consultantContractAccommodationAlt[v],
    value: v
  }));

  if (tenant === Tenant.WCC) {
    return _opt.map((o) => ({ ...o, label: o.label.replace("Bonliva", "WCC") }));
  }
  return _opt;
}

export const oneSpecificPeriodPrices: SpecificPeriodPriceInput = {
  price: null,
  title: "Sommartillägg",
  startDate: null,
  endDate: null
};

export const YoExOptions: ValueLabel[] = Array.from(Array(10), (x, i) => ({
  label: locale.YoEx(i + 1),
  value: `${i + 1}`
}));

export const defaultProof = {
  files: [],
  approve: false
};

export const initialValues: FormData = {
  ConsultantId: null,
  ContractType: null,
  Profession: "",
  Country: "Sweden",
  HospitalRef: null,
  DepartmentsRefs: null,
  ExcludeAddress: false,
  showOrgName: false,
  hideDeptName: false,
  Address: initAddress,
  Period: "",
  StartDate: null,
  EndDate: null,
  pensionSolution: null,
  PatientVisits: null,
  InvestigationsNumber: null,
  IsRegionDalarna: false,
  ConsultantGetsPaidPer: ConsultantGetsPaidPer.Investigation,
  OtherRequirements: [""],
  YoEx: "2",
  AreaOfExpertise: null,
  Specializations: [],
  Language: locale.consultantContractLangOpt.v1,
  Education: { Show: false, Text: "" },
  DriverLicense: false,
  SpecificRequirements: [""],
  Currency: ConsultantContractCurrency.SEK,
  BasicPrice: null,
  SalaryRecords: [ContractPriceTemplateRecordInput.CreateBlank()],
  highHolidays: [],
  SalaryChoice: null,
  SpecificPeriodPrices: [],
  PayoutType: null,
  NotRelevantMonthlyWeeklyPayouts: false,
  Overtime: ConsultantContractOvertimeOpt.NoSpecialOvertime,
  Accommodation: defaultAccommodation,
  Travels: defaultTravels,
  DateFrameworkContractTypeIsSigned: null,
  template: null,
  favTemplate: null,
  customerTemplate: null,
  Bonus: [],
  IntroductionPrice: [],
  editsProof: defaultProof,
  isNationalProcurementPriceStrategy: null
};

export const handleSetPriceTemplateOnOvertimeChange = (
  form: FormStore<FormData>,
  templates: ContractsPriceTemplateFragment[],
  handler: (templateId: string) => void,
  setWarning: (text: string) => void,
  setDisabled: (v: boolean) => void,
  skipSetTemplate = false
) => {
  console.log("check overtime");
  // handle SSK prof with ConsultantContractType.Employment and Intermittent
  const { ContractType, Profession, Overtime } = form.values;
  if (
    Profession === "SSK" &&
    Overtime === ConsultantContractOvertimeOpt.SKLBasedOvertime
  ) {
    // todo brainstorm how we can improve matching templates
    const defaultTemplate = switchTemplate(ContractType, Profession);
    const template = templates?.find(
      (x) => x.name === defaultTemplate.name || x._id === defaultTemplate._id
    );
    if (template || skipSetTemplate) {
      if (!skipSetTemplate) {
        handler(template._id);
      }
      setWarning(
        "Du har valt 'OB-ersättning matchar faktorer vid var tid gällande SKR:s Allmänna Bestämmelser (AB).'. Därför är alla fält låsta"
      );
      setDisabled(true);
    } else {
      form.setError(
        "noDefaultSalaryTemplate",
        "Oops, det verkar inte finnas någon avtalsmall för OB-tillägg enligt SKR aktiv för konsultens yrke. Vänligen skriv till service@bonliva.se och it@bonliva.se och ange länk till konsulten samt att problemet gäller mall för OB-tillägg enligt SKR."
      );
    }
  } else {
    setDisabled(false);
    setWarning(null);
  }
};

const switchTemplate = (
  contractType: ConsultantContractType,
  profession: string
): { name: string; _id: string } => {
  if (profession === "SSK") {
    switch (contractType) {
      case ConsultantContractType.Employment:
      case ConsultantContractType.Employment_National:
      case ConsultantContractType.Intermittent:
      case ConsultantContractType.Intermittent_National:
      case ConsultantContractType.ConfirmationForEmployees:
      case ConsultantContractType.ConfirmationForEmployees_National:
        return {
          name: "SSK OB-tillägg (SKR) löntagare",
          _id: "5fbb93711c280c446c30b775"
        };
      case ConsultantContractType.Framework:
      case ConsultantContractType.Framework_National:
      case ConsultantContractType.Outsource:
      case ConsultantContractType.Outsource_National:
      case ConsultantContractType.Confirmation:
      case ConsultantContractType.Confirmation_National:
        return {
          name: "SSK OB-tillägg (SKR) Underleverantör AB 17",
          _id: "5fbb970c1c280c446c30bef8"
        };
      default:
        return null;
    }
  }
};

export const calculatePriceUnderSalaryLimit = (price: any, suffix: string): string => {
  const priceNumber = toFinite(price);

  return `${(priceNumber / 1.045).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${suffix}`;
};

export const calculatePriceOverSalaryLimit = (price: any, suffix: string): string => {
  const priceNumber = toFinite(price);

  return `${(priceNumber / 1.3).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} ${suffix}`;
};

export const isConfirmationContract = (contractType: ConsultantContractType): boolean => {
  return Confirmation_contractsTypes.includes(contractType);
};

interface ContractContext {
  selection: ContractType;
  contractLengthMonths: number; // length of current contract in months
  monthsSinceLastContract?: number; // undefined if first contract
}

export function getContractWarning({
  selection,
  contractLengthMonths,
  monthsSinceLastContract
}: ContractContext): string | null {
  console.log(
    selection,
    "contractLengthMonths:",
    contractLengthMonths,
    "monthsSinceLastContract:",
    monthsSinceLastContract
  );
  // Rule 1: "Timlön" (Val 1) is always acceptable
  if (selection === ContractType.Hourly) {
    console.log('Rule 1: "Timlön" (Val 1) is always acceptable');
    return null;
  }

  // Rule 2: If contract length > 3 months -> always acceptable
  if (contractLengthMonths > 3) {
    console.log("Rule 2: If contract length > 3 months -> always acceptable");
    return null;
  }

  // Rule 3: If less than 2 months since last contract ended -> all acceptable
  if (monthsSinceLastContract !== null && monthsSinceLastContract < 2) {
    console.log(
      "Rule 3: If less than 2 months since last contract ended -> all acceptable"
    );
    return null;
  }

  // Rule 4: More than 2 months since last contract (or first contract)
  if (monthsSinceLastContract === null || monthsSinceLastContract >= 2) {
    if (contractLengthMonths < 2) {
      console.log("Rule 4: More than 2 months since last contract (or first contract)");
      if (selection === ContractType.HourlyPension) {
        return `Det här alternativet kan endast väljas om anställningen överstiger 2 månader.
Om anställningen är kortare än 2 månader kan detta val ändå göras om konsulten kommer ha
flera efterföljande anställningsperioder (med max 2 månader mellan varje period) som tillsammans
överstiger gränsen.`;
      }
      if (selection === ContractType.HourlyPensionInsurance) {
        return `Det här alternativet kan endast väljas om anställningen överstiger 3 månader.
Om anställningen är kortare än 3 månader kan detta val ändå göras om konsulten kommer ha flera
efterföljande anställningsperioder (med max 2 månader mellan varje period) som tillsammans
överstiger gränsen.`;
      }
    }

    if (contractLengthMonths >= 2 && contractLengthMonths <= 3) {
      if (selection === ContractType.HourlyPensionInsurance) {
        return `Det här alternativet kan endast väljas om anställningen överstiger 3 månader.
Om anställningen är kortare än 3 månader kan detta val ändå göras om konsulten kommer ha flera
efterföljande anställningsperioder (med max 2 månader mellan varje period) som tillsammans
överstiger gränsen.`;
      }
    }
  }

  return null;
}
